import { dateFormat, DateFormat, formatDate, isToday } from '@cp/src/utils/DateHelper';
import dayjs from 'dayjs';
import {
  PaymentStatus,
  ICaseData,
  ICasesThinList,
  IVisitPlanCaseData,
  IAgent,
  ICaseItemLatLongData
} from './interface';
import {
  ViewType,
  VISIT_PLAN,
  RANK,
  AGENCY,
  AGENT_ID,
  DATE,
  DistanceCalculationType,
  MAX_CASE_DISTANCE_KM,
  VISIT_PLAN_SOURCE,
  VISIT_PLAN_TYPE
} from './constants';
import { readQueryParams } from '@cp/src/utils/QueryParamsHelper';
import { Search } from '@cp/src/utils/search';
import { Dispatch } from '@reduxjs/toolkit';
import { RootState } from '@cp/src/store';
import { logError } from '@cp/src/utils/ApiHelper';

import {
  setTodayVisitPlanCases,
  setTomorrowVisitPlanCases,
  setPreviousVisitPlanCases,
  setCasesData
} from './reducer/VisitPlanSlice';

export enum MarkerState {
  SELECTED = 'SELECTED',
  IN_VISIT_PLAN = 'IN_VISIT_PLAN',
  PAID = 'PAID',
  PARTIALLY_PAID_PTP = 'PARTIALLY_PAID_PTP',
  VISITED = 'VISITED',
  UNVISITED = 'UNVISITED'
}

export const formatVisitPlanDate = (dateString?: string): string => {
  if (!dateString) return '';

  return isToday(dateString) ? '' : formatDate(dateString, DateFormat.DD_MMM);
};

export const formatVisitPlanTime = (dateString?: string): string => {
  if (!dateString) return '--';

  return dateFormat(new Date(dateString), DateFormat.LONG_DATE_FORMAT_WITH_TIME);
};

export const getVisitPlanDateRange = () => {
  const today = dayjs();
  const yesterday = today.subtract(1, 'day');
  const tomorrow = today.add(1, 'day');
  return {
    minDate: yesterday.format(DateFormat.DATE_PICKER_FORMAT),
    maxDate: tomorrow.format(DateFormat.DATE_PICKER_FORMAT)
  };
};

export const getVisitPlanTypeFromSystemGeneratedFlag = (
  enableSystemGeneratedVisitPlan?: boolean
): VISIT_PLAN_TYPE =>
  enableSystemGeneratedVisitPlan ? VISIT_PLAN_TYPE.SYSTEM_GENERATED : VISIT_PLAN_TYPE.MANUAL;

export const getMarkerState = (
  selected: boolean,
  activeTab: ViewType,
  visitPlan: boolean,
  paymentStatus?: PaymentStatus,
  isVisited?: boolean,
  isPTP?: boolean
): MarkerState => {
  if (activeTab === ViewType.CASE_LIST && visitPlan) return MarkerState.IN_VISIT_PLAN;
  if (selected) return MarkerState.SELECTED;
  if (paymentStatus === PaymentStatus.PAID || paymentStatus === PaymentStatus.CLOSED) {
    return MarkerState.PAID;
  }
  if (paymentStatus === PaymentStatus.PARTIALLY_PAID || isPTP)
    return MarkerState.PARTIALLY_PAID_PTP;
  if (isVisited) return MarkerState.VISITED;
  return MarkerState.UNVISITED;
};

export const getRelativeDayStatus = (date: string) => {
  if (!date) return null;

  const inputDate = dayjs(date);
  const today = dayjs();

  if (!inputDate.isValid()) return null;

  const inputDay = inputDate.startOf('day');
  const todayDay = today.startOf('day');

  return inputDay.diff(todayDay, 'day');
};

export const filterCasesBySearch = (
  cases: { [key: string]: ICaseData },
  thinList: ICasesThinList[],
  searchQuery: string
): ICasesThinList[] => {
  if (!searchQuery.trim()) {
    return thinList;
  }

  const searchableItems = thinList
    .map(thinCase => {
      const caseData = cases[thinCase.caseReferenceId];
      return {
        ...thinCase,
        customerName: caseData?.customerName || '',
        entityId: caseData?.entityId || ''
      };
    })
    .filter(item => item.customerName || item.entityId);

  const fuzzySearchResults = Search(searchQuery, searchableItems, {
    keys: ['customerName', 'entityId'],
    all: true
  });

  return (
    fuzzySearchResults?.map(
      (result: { obj: ICasesThinList & { customerName: string; entityId: string } }) => ({
        caseReferenceId: result.obj.caseReferenceId,
        addressId: result.obj.addressId,
        location: result.obj.location
      })
    ) || []
  );
};

export const buildFiltersFromQueryParams = (
  caseFilterSchema: Record<string, { multiSelect?: boolean }>
): Record<string, string[] | string> => {
  const queryParams = readQueryParams();
  const visitPlanParams = queryParams?.[VISIT_PLAN] || {};
  const filters: Record<string, string[] | string> = {};

  Object.keys(visitPlanParams).forEach(key => {
    if (key !== AGENCY && key !== AGENT_ID && key !== DATE) {
      const value = visitPlanParams[key];
      if (value) {
        const schemaItem = caseFilterSchema?.[key];
        if (schemaItem && schemaItem.multiSelect && key !== RANK) {
          filters[key] = Array.isArray(value) ? value : [value];
        } else {
          filters[key] = Array.isArray(value) ? value[0] : value;
        }
      }
    }
  });

  return filters;
};

// Helper function to create thin list from cases
export const createThinListFromCases = (cases: { [key: string]: ICaseData }): ICasesThinList[] => {
  return Object.keys(cases).map(key => ({
    caseReferenceId: key,
    addressId: cases[key]?.address?.addressId,
    location: cases[key]?.address?.location
  }));
};

// Helper function to apply distance filtering
export const applyDistanceFiltering = async (
  agent: IAgent,
  cases: { [key: string]: ICaseData },
  thinList: ICasesThinList[],
  dispatch: Dispatch,
  getGeolocationDistance: (params: {
    source: ICaseItemLatLongData;
    destinations: ICaseItemLatLongData[];
    calculationType: DistanceCalculationType;
    fallbackType: DistanceCalculationType;
  }) => Promise<Map<string, number>>
): Promise<void> => {
  const source: ICaseItemLatLongData = {
    id: agent.referenceId,
    latitude: agent.location?.latitude || 0,
    longitude: agent.location?.longitude || 0
  };

  const destinations = thinList
    .filter(thinCase => {
      const caseLocation = cases[thinCase.caseReferenceId]?.address?.location;
      return caseLocation?.latitude && caseLocation?.longitude;
    })
    .map(thinCase => ({
      id: thinCase.caseReferenceId,
      latitude: cases[thinCase.caseReferenceId].address.location.latitude,
      longitude: cases[thinCase.caseReferenceId].address.location.longitude
    }));

  if (destinations.length === 0) {
    // No valid locations found, dispatch all cases
    dispatch(setCasesData({ data: cases, thinList }));
    return;
  }

  try {
    const distanceMap = await getGeolocationDistance({
      source,
      destinations,
      calculationType: DistanceCalculationType.GRAPHHOPPER,
      fallbackType: DistanceCalculationType.HAVERSINE
    });

    const filteredThinList = thinList.filter(thinCase => {
      const distance = distanceMap.get(thinCase.caseReferenceId);
      return distance === undefined || distance <= MAX_CASE_DISTANCE_KM;
    });

    const filteredCases: { [key: string]: ICaseData } = {};
    filteredThinList.forEach(thinCase => {
      if (cases[thinCase.caseReferenceId]) {
        filteredCases[thinCase.caseReferenceId] = cases[thinCase.caseReferenceId];
      }
    });

    dispatch(setCasesData({ data: filteredCases, thinList: filteredThinList }));
  } catch (error) {
    logError('Error filtering cases by distance:', (error as Error).message);
    // If distance API fails, continue with all cases
    dispatch(setCasesData({ data: cases, thinList }));
  }
};

export const mergeAndDispatchVisitPlanData = (
  dispatch: Dispatch,
  apiCases: Record<string, IVisitPlanCaseData>,
  currentState: RootState['visitPlan'],
  relativeDayStatus: number | null
): void => {
  // Get current local data based on day status
  let currentData: Record<string, IVisitPlanCaseData> = {};

  switch (relativeDayStatus) {
    case 0:
      currentData = currentState.todayVisitPlanCases?.data || {};
      break;
    case 1:
      currentData = currentState.tomorrowVisitPlanCases?.data || {};
      break;
  }

  const mergedData = {
    ...currentData, // Keep existing local data
    ...apiCases // Override with API data
  };

  if (!mergedData || Object.keys(mergedData).length === 0) {
    return;
  }

  const mergedThinList = Object.keys(mergedData).map(key => ({
    caseReferenceId: key,
    addressId: mergedData[key]?.address?.addressId,
    location: mergedData[key]?.address?.location,
    source: mergedData[key]?.source || VISIT_PLAN_SOURCE.LONGHORN
  }));

  switch (relativeDayStatus) {
    case 0:
      dispatch(setTodayVisitPlanCases({ data: mergedData, thinList: mergedThinList }));
      break;
    case 1:
      dispatch(setTomorrowVisitPlanCases({ data: mergedData, thinList: mergedThinList }));
      break;
    case -1:
      dispatch(setPreviousVisitPlanCases({ data: mergedData, thinList: mergedThinList }));
      break;
    default:
      break;
  }
};
